import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_ENDPOINT || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const adminAuthAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  getProfile: () => api.get('/admin/auth/profile')
};

export const messagesAPI = {
  getAllSessions: (params) => api.get('/admin/messages/sessions', { params }),
  getSessionMessages: (sessionId, params) => api.get(`/admin/messages/session/${sessionId}`, { params }),
  sendMessage: (sessionId, data) => api.post(`/admin/messages/session/${sessionId}/send`, data),
  sendTypedMessage: (sessionId, data) => api.post(`/admin/messages/session/${sessionId}/send-typed`, data),
  getDashboardStats: () => api.get('/admin/messages/stats'),
  clearSession: (sessionId) => api.delete(`/admin/messages/session/${sessionId}/clear`),
  getSessionTags: (sessionId) => api.get(`/admin/messages/session/${sessionId}/tags`),
  setSessionTags: (sessionId, tags) => api.put(`/admin/messages/session/${sessionId}/tags`, { tags }),
  updateSessionTags: (sessionId, add = [], remove = []) =>
    api.post(`/admin/messages/session/${sessionId}/tags`, { add, remove })
};

export const adminAPI = {
  getAll: () => api.get('/admin/admins'),
  create: (data) => api.post('/admin/admins', data),
  update: (id, data) => api.put(`/admin/admins/${id}`, data),
  delete: (id) => api.delete(`/admin/admins/${id}`)
};

export const platformPagesAPI = {
  getAll: (params) => api.get('/admin/platform-pages', { params }),
  create: (data) => api.post('/admin/platform-pages', data),
  update: (id, data) => api.put(`/admin/platform-pages/${id}`, data),
  delete: (id) => api.delete(`/admin/platform-pages/${id}`),
  toggleStatus: (id) => api.patch(`/admin/platform-pages/${id}/toggle`),
  testCredentials: (id) => api.post(`/admin/platform-pages/${id}/test`)
};

export const facebookOAuthAPI = {
  getOAuthUrl: () => api.get('/admin/facebook/oauth/url'),
  syncPages: () => api.post('/admin/facebook/oauth/sync-pages')
};

export const aiAgentAPI = {
  getGlobalStatus: () => api.get('/admin/ai-agent/global'),
  toggleGlobal: (enabled) => api.post('/admin/ai-agent/global', { enabled }),
  getSessionStatus: (sessionId) => api.get(`/admin/ai-agent/session/${sessionId}`),
  toggleSession: (sessionId, enabled) => api.post(`/admin/ai-agent/session/${sessionId}`, { enabled }),
  getEffectiveStatus: (sessionId) => api.get(`/admin/ai-agent/effective/${sessionId}`)
};

export const settingsAPI = {
  getAIRules: () => api.get('/admin/settings/ai-rules'),
  updateAIRules: (aiRules) => api.put('/admin/settings/ai-rules', { aiRules }),
  changePassword: (currentPassword, newPassword) =>
    api.put('/admin/settings/password', { currentPassword, newPassword }),
  getBalance: () => api.get('/admin/settings/balance')
};

export const getSocketBaseUrl = () => {
  const raw = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3000/api';
  return raw.replace(/\/api(?:\/.*)?$/, '');
};

export default api;
